import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LOG_ROCKET_CONFIGURATION, LogRocketConfiguration } from '@uzurv/core';
import { AuthenticatedRiderService } from '@uzurv/rideWss/app/services/authenticated-rider/authenticated-rider.service';
import * as RiderAuthenticationSelectors from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.selector';
import * as RidesSelectors from '@uzurv/rideWss/app/store/rides/rides.selectors';
import { AuthDataService } from '@uzurv/rideWss/auth';
import { OrganizationRiderListItem } from '@uzurv/rideWss/clara';
import LogRocket from 'logrocket';
import { Subject, takeUntil } from 'rxjs';
import { APP_CONFIGURATION, AppConfiguration } from '../../../../app/app-configuration';
import { isNullOrUndefined } from '@uzurv/rideWss/app/util';

// TODO(Ride WSS): Add a new log rocket configuration
@Injectable({
  providedIn: 'root'
})
export class LogRocketService {
  private destroyed$ = new Subject<void>();
  private _organizationRiderListItems: Array<OrganizationRiderListItem> = [];

  constructor(
    private authDataService: AuthDataService,
    private authenticatedRiderService: AuthenticatedRiderService,
    private store$: Store,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration,
    @Inject(LOG_ROCKET_CONFIGURATION) protected logRocketConfiguration: LogRocketConfiguration
  ) {}

  init() {
    if (this.logRocketConfiguration.logRocketSessionCaptureEnabled) {
      LogRocket.init(this.logRocketConfiguration.logRocketProjectKey, {
        release: this.appConfiguration.version,
        network: {
          requestSanitizer: request => {
            // scrub out the body
            if (this.shouldSanitizeRequestBody(request.url, request.method)) {
              request.body = null;
            }

            return request;
          },
          responseSanitizer: response => {
            const errors = response.body['errors'];

            // Scrub response body unless there are errors
            if (isNullOrUndefined(errors) || errors.length === 0) {
              delete response.body;
            }

            return response;
          }
        }
      });
    }

    this.store$
      .select(RiderAuthenticationSelectors.getAuthenticatedRider)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.identifyAuthenticatedUserAndOrganization());

    this.store$
      .select(RidesSelectors.getOrganizationRiders)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(organizationListItems => {
        this._organizationRiderListItems = organizationListItems;
        this.identifyAuthenticatedUserAndOrganization();
      });

    this.authDataService.loggedInUserDataStoredEvents.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.authDataService.isLoggedIn) {
        this.identifyAuthenticatedUserAndOrganization();
      }
    });

    this.authDataService.logoutEvents.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.logRocketConfiguration.logRocketSessionCaptureEnabled) {
        LogRocket.startNewSession();
      }
    });
  }

  private identifyAuthenticatedUserAndOrganization() {
    if (!this.authenticatedRiderService.isLoggedIn()) {
      LogRocket.identify(null, null);
    } else {
      const traits: {
        [propName: string]: string | number | boolean;
      } = {};

      traits['name'] =
        `${this.authenticatedRiderService.getLoggedInUserFirstName()} ${this.authenticatedRiderService.getLoggedInUserLastName()}`;
      traits['email'] = this.authenticatedRiderService.getLoggedInUserEmailAddress();
      if (this._organizationRiderListItems) {
        traits['organizations'] = this._organizationRiderListItems.map(item => item.organizationName).join(',');
      }

      LogRocket.identify(this.authenticatedRiderService.getLoggedInRiderId().toString(), traits);
    }
  }

  private shouldSanitizeRequestBody(requestUrl: string, requestMethod: string): boolean {
    return true;
  }
}
